import React from 'react';
import { sparkToast, sparkAssetUrl } from '@spark';
import DatasetsHeader from '@/features/datasets/components/datasets-page/DatasetsHeader.jsx';
import DatasetsFilterToolbar from '@/features/datasets/components/datasets-page/DatasetsFilterToolbar.jsx';
import get_default_benchmarks_table_data from '@/features/datasets/components/benchmarks/DefaultBenchmarksData.jsx';
import get_default_datasets_table_data from '@/features/datasets/components/datasets-page/DefaultDatasetsData.jsx';
import { EmptyStateCard } from '@/features/tasks/components/HomePage/EmptyStateCard';

import { createdByUserToSearchText, formatDateTime, includesSearchTerm } from '@/shared/utils/helpers';
import { data_from_server_post } from '@/shared/http/client';
import DatasetsGroupedTableView from '@/features/datasets/components/datasets-page/DatasetsGroupedTableView.jsx';

export class Datasets extends React.Component {

    constructor(props) {
        super(props);
        this.state = {
            trainDatasets: [],
            benchmarkDatasets: [],
            manualPredictionDatasets: [],
            loading: true,
            selectedTaskType: null,
            sortValue: 'recently_updated',
            filterValue: 'all',
            searchValue: '',
            userFilter: 'All Users',
            isBulkOperationInProgress: false
        }
        this.get_datasets = this.get_datasets.bind(this)
        this.get_benchmarks = this.get_benchmarks.bind(this)
        this.setIsLoading = this.setIsLoading.bind(this);
        this.handleTaskTypeChange = this.handleTaskTypeChange.bind(this);
        this.handleSortChange = this.handleSortChange.bind(this);
        this.handleFilterChange = this.handleFilterChange.bind(this);
        this.handleSearchChange = this.handleSearchChange.bind(this);
        this.handleUserFilterChange = this.handleUserFilterChange.bind(this);
        this.handleDatasetAction = this.handleDatasetAction.bind(this);
        this.handleBulkDelete = this.handleBulkDelete.bind(this);
    }

    componentDidMount() {
        this.get_all_datasets();
        window.addEventListener('focus', this.handleWindowFocus);
    }

    componentWillUnmount() {
        window.removeEventListener('focus', this.handleWindowFocus);
    }

    handleWindowFocus = () => {
        this.get_all_datasets();
    }

    setIsLoading(loading) {
        this.setState({ loading })
    }

    handleTaskTypeChange(taskType) {
        this.setState({ selectedTaskType: taskType });
    }

    handleSortChange(value) {
        this.setState({ sortValue: value });
    }

    handleFilterChange(value) {
        this.setState({ filterValue: value });
    }

    handleSearchChange(value) {
        this.setState({ searchValue: value });
    }

    handleUserFilterChange(value) {
        this.setState({ userFilter: value });
    }

    handleDatasetAction(action, dataset, isBenchmark, isManual) {
        console.log('Dataset action:', action, dataset);
        if (action === 'delete') {
            const deletedName = dataset?.name || dataset?.dataset_name;
            if (!deletedName) {
                sparkToast.error('Could not determine dataset name to delete');
                return;
            }
            const groupName = isManual
                ? 'External Model Results'
                : (isBenchmark ? 'Benchmarks' : 'Datasets');
            this.handleBulkDelete(
                [{ ...dataset, name: deletedName, dataset_name: deletedName, _groupName: groupName }],
                null
            );
            return;
        } else if (action === 'view') {
            console.log('View dataset:', dataset.name);
        } else if (action === 'edit') {
            console.log('Edit dataset:', dataset.name);
        }
    }

    handleBulkDelete(datasetsToDelete, onSuccess) {
        if (!datasetsToDelete || datasetsToDelete.length === 0) return;

        this.setState({ isBulkOperationInProgress: true });

        const datasetsPayload = datasetsToDelete.map(dataset => ({
            name: dataset.name || dataset.dataset_name,
            is_benchmark: dataset._groupName === 'Benchmarks',
            is_manual_model_predictions: dataset._groupName === 'External Model Results'
        }));

        data_from_server_post(
            'bulk-delete-datasets',
            {
                client_id: "40b3a00e282ee2db1338c3bba881b99125fc3ba1d7f5c29232ce1ad63fab1e75",
                datasets: datasetsPayload
            },
            (response) => {
                this.setState({ isBulkOperationInProgress: false });
                if (response.success) {
                    sparkToast.success(`Successfully deleted ${response.deleted_count} dataset${response.deleted_count > 1 ? 's' : ''}`);
                    const deletedNames = datasetsPayload.map(d => d.name);
                    this.setState(
                        (prev) => ({
                            trainDatasets: prev.trainDatasets.filter(d => !deletedNames.includes(d.name || d.dataset_name)),
                            benchmarkDatasets: prev.benchmarkDatasets.filter(d => !deletedNames.includes(d.name || d.dataset_name)),
                            manualPredictionDatasets: prev.manualPredictionDatasets.filter(d => !deletedNames.includes(d.name || d.dataset_name))
                        }),
                        () => {
                            this.get_all_datasets();
                            if (onSuccess) onSuccess();
                        }
                    );
                } else {
                    sparkToast.error('Failed to delete some datasets');
                    if (response.failed && response.failed.length > 0) {
                        console.error('Failed deletions:', response.failed);
                    }
                    this.get_all_datasets();
                    if (onSuccess) onSuccess();
                }
            },
            (error) => {
                this.setState({ isBulkOperationInProgress: false });
                sparkToast.error(`Error deleting datasets: ${error}`);
            }
        );
    }

    get_datasets = async () => {
        data_from_server_post('datasets', {
            "client_id": "40b3a00e282ee2db1338c3bba881b99125fc3ba1d7f5c29232ce1ad63fab1e75",
            "is_benchmark": false,
        }, (response) => {
            const datasets = response.datasets.map((dataset, index) => {
                return {
                    ...dataset,
                    key: dataset.value,
                    name: dataset.value,
                    dataset_name: dataset.value,
                    entries: dataset.total_records || 0,
                    size: dataset.size || "Unknown",
                    data_creation_type: dataset.data_creation_type || "Unknown!!",
                    last_modified: dataset.created_at || new Date().toISOString(),
                    created_at: dataset.created_at || new Date().toISOString()
                }
            });
            this.setState({ trainDatasets: datasets });
        }, () => {
            console.error("Error parsing datasets");
            this.setState({ trainDatasets: get_default_datasets_table_data() });
        });
    }

    get_all_datasets = async () => {
        data_from_server_post('datasets', {
            "client_id": "40b3a00e282ee2db1338c3bba881b99125fc3ba1d7f5c29232ce1ad63fab1e75",
        }, (response) => {
            const mapItem = (item) => ({
                ...item,
                key: item.value,
                name: item.value,
                dataset_name: item.source_dataset_name || item.value,
                entries: item.total_records || 0,
                size: item.size || "Unknown",
                data_creation_type: item.data_creation_type || "Unknown",
                summary: item.summary || {},
                last_modified: item.created_at || new Date().toISOString(),
                created_at: item.created_at || new Date().toISOString(),
                created_by_user: item.created_by_user || null,
                linked_tasks: item.linked_tasks || [],
                is_raw: item.is_raw || false,
            });
            const train = (response?.datasets || []).map(mapItem);
            const bench = (response?.benchmarks || []).map(mapItem);
            const manual = (response?.manual_model_predictions || []).map(mapItem);
            this.setState({
                trainDatasets: train,
                benchmarkDatasets: bench,
                manualPredictionDatasets: manual,
                loading: false
            });
        }, () => {
            console.error("Error fetching datasets (all)");
            this.get_datasets();
            this.get_benchmarks();
        });
    }

    get_benchmarks = async () => {
        data_from_server_post('datasets', {
            "client_id": "40b3a00e282ee2db1338c3bba881b99125fc3ba1d7f5c29232ce1ad63fab1e75",
            "is_benchmark": true,
        }, (response) => {
            const benchmarks = response.datasets.map((benchmark, index) => {
                return {
                    ...benchmark,
                    key: benchmark.value,
                    name: benchmark.value,
                    dataset_name: benchmark.value,
                    entries: benchmark.total_records || 0,
                    size: benchmark.size || "Unknown",
                    data_creation_type: benchmark.data_creation_type || "Unknown",
                    last_modified: benchmark.created_at || new Date().toISOString(),
                    created_at: benchmark.created_at || new Date().toISOString()
                }
            });
            this.setState({ benchmarkDatasets: benchmarks, loading: false });
        }, () => {
            this.setState({ benchmarkDatasets: get_default_benchmarks_table_data(), loading: false });
        });
    }

    getFilteredAndSortedDatasets() {
        const { trainDatasets, benchmarkDatasets, manualPredictionDatasets, filterValue, searchValue, sortValue, userFilter, loading } = this.state;

        let datasetsToShow = [];

        if (filterValue === 'all') {
            datasetsToShow = [...trainDatasets, ...benchmarkDatasets, ...manualPredictionDatasets];
        } else if (filterValue === 'train_sets') {
            datasetsToShow = [...trainDatasets];
        } else if (filterValue === 'benchmarks') {
            datasetsToShow = [...benchmarkDatasets];
        } else if (filterValue === 'manual_model_predictions') {
            datasetsToShow = [...manualPredictionDatasets];
        }

        if (searchValue) {
            const getTasksSearchParts = (dataset) => {
                const groupAndUsecaseNames = dataset?.group_and_usecase_names;
                if (!groupAndUsecaseNames || typeof groupAndUsecaseNames !== 'object') return [];
                return Object.entries(groupAndUsecaseNames).flatMap(([groupName, tasks]) => ([
                    groupName,
                    ...(Array.isArray(tasks) ? tasks : []),
                ]));
            };

            datasetsToShow = datasetsToShow.filter((dataset) => {
                const datasetGroup = trainDatasets.includes(dataset)
                    ? 'Train sets'
                    : benchmarkDatasets.includes(dataset)
                        ? 'Benchmarks'
                        : manualPredictionDatasets.includes(dataset)
                            ? 'External Model Results'
                            : '';

                const datasetName = dataset?.name || dataset?.dataset_name || dataset?.value || '';
                const entries = dataset?.entries ?? dataset?.total_records ?? dataset?.summary?.num_records ?? '';
                const charCount = dataset?.summary?.num_characters?.sum ?? '';
                const lastModified = formatDateTime(dataset?.last_modified || dataset?.created_at);
                const createdAt = formatDateTime(dataset?.created_at);

                return includesSearchTerm(
                    searchValue,
                    datasetGroup,
                    datasetName,
                    dataset?.data_creation_type,
                    createdByUserToSearchText(dataset?.created_by_user),
                    lastModified,
                    createdAt,
                    entries,
                    charCount,
                    getTasksSearchParts(dataset),
                );
            });
        }

        if (userFilter && userFilter !== 'All Users') {
            const userIds = Array.isArray(userFilter) ? userFilter : [userFilter];
            datasetsToShow = datasetsToShow.filter(dataset => {
                const userId = dataset.created_by_user?.sub || dataset.created_by_user?.email;
                return userIds.includes(userId);
            });
        }

        datasetsToShow.sort((a, b) => {
            switch (sortValue) {
                case 'recently_updated':
                    return new Date(b.last_modified || b.created_at) - new Date(a.last_modified || a.created_at);
                case 'oldest_first':
                    return new Date(a.last_modified || a.created_at) - new Date(b.last_modified || b.created_at);
                case 'name':
                    return (a.name || a.dataset_name || '').localeCompare(b.name || b.dataset_name || '');
                case 'data_creation_type':
                    return (a.data_creation_type || '').localeCompare(b.data_creation_type || '');
                default:
                    return 0;
            }
        });

        const groupedDatasets = [];

        const trainDatasetsInFilter = datasetsToShow.filter(d => trainDatasets.includes(d));
        if (trainDatasetsInFilter.length > 0 || (loading && (filterValue === 'all' || filterValue === 'train_sets'))) {
            groupedDatasets.push({
                group_id: 'train_sets',
                group_name: 'Train sets',
                subtitle: '',
                datasets: trainDatasetsInFilter,
                isLoading: loading && trainDatasetsInFilter.length === 0
            });
        }

        const benchmarkDatasetsInFilter = datasetsToShow.filter(d => benchmarkDatasets.includes(d));
        if (benchmarkDatasetsInFilter.length > 0 || (loading && (filterValue === 'all' || filterValue === 'benchmarks'))) {
            groupedDatasets.push({
                group_id: 'benchmarks',
                group_name: 'Benchmarks',
                subtitle: '',
                datasets: benchmarkDatasetsInFilter,
                isLoading: loading && benchmarkDatasetsInFilter.length === 0
            });
        }

        const manualPredsInFilter = datasetsToShow.filter(d => manualPredictionDatasets.includes(d));
        if (manualPredsInFilter.length > 0 || (loading && (filterValue === 'all' || filterValue === 'manual_model_predictions'))) {
            groupedDatasets.push({
                group_id: 'external_model_results',
                group_name: 'External Model Results',
                subtitle: '',
                datasets: manualPredsInFilter,
                isLoading: loading && manualPredsInFilter.length === 0
            });
        }

        return groupedDatasets;
    }

    render() {
        const { trainDatasets, benchmarkDatasets, manualPredictionDatasets, loading, sortValue, filterValue, searchValue } = this.state;

        const hasNoDatasets = !loading
            && trainDatasets.length === 0
            && benchmarkDatasets.length === 0
            && manualPredictionDatasets.length === 0;

        const filteredAndSortedDatasets = this.getFilteredAndSortedDatasets();

        return (
            <div
                className="datasets page-content-shell"
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: '100vh',
                    paddingTop: 0,
                    background: 'var(--semantic-surface-main)',
                }}
            >
                <DatasetsHeader />

                {hasNoDatasets ? (
                    <div style={{ padding: '32px 0' }}>
                        <EmptyStateCard
                            title="No datasets found"
                            description="Once you have datasets, they'll appear here."
                            showPrimaryAction={false}
                            illustrationSrc={sparkAssetUrl('illustrations/illustration-box-open.svg')}
                        />
                    </div>
                ) : (
                    <>
                        <DatasetsFilterToolbar
                            sortValue={sortValue}
                            onSortChange={this.handleSortChange}
                            filterValue={filterValue}
                            onFilterChange={this.handleFilterChange}
                            searchValue={searchValue}
                            onSearchChange={this.handleSearchChange}
                            userFilter={this.state.userFilter}
                            onUserFilterChange={this.handleUserFilterChange}
                            allDatasets={[...trainDatasets, ...benchmarkDatasets, ...manualPredictionDatasets]}
                        />
                        <div style={{ padding: '32px 0' }}>
                            <DatasetsGroupedTableView
                                groupedDatasets={filteredAndSortedDatasets}
                                onDatasetAction={this.handleDatasetAction}
                                onBulkDelete={this.handleBulkDelete}
                                isBulkOperationInProgress={this.state.isBulkOperationInProgress}
                            />
                        </div>
                    </>
                )}
            </div>
        );
    }
}
